import React, { useState, useEffect } from "react";
import axios from "axios";
import { DOG_BREEDS, CAT_BREEDS, newPet, isPetEligibleForGrooming } from "./bookingData";

const DogIcon = ({ size = 28, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 5.172C10 3.782 8.423 2.679 6.5 3c-2 .336-3.5 2.112-3.5 4-.085 2.053.416 3.461 1.5 4.5s1 3.5 0 4.5"/>
    <path d="m10 5.172 2 3.792c0 .01 2.36 5.036 2 6.036-1 2-4 2-5 0s-.5-4 0-5"/>
    <path d="M16.5 12s.5 2 3 3c0 3-3 5-6 4.5s-3-3.5-3-3.5"/>
    <path d="M3 12s.5-1 1-2"/><path d="M5 3.5s.5-1 2-1c.83 0 1.5.5 1.5.5"/>
  </svg>
);

const CatIcon = ({ size = 28, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5z"/>
    <path d="M8 14v.5"/><path d="M16 14v.5"/>
    <path d="M11.25 16.25h1.5L12 17l-.75-.75z"/>
  </svg>
);

const compressImage = (file, maxPx = 500, quality = 0.72) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const ratio  = Math.min(maxPx / img.width, maxPx / img.height, 1);
        const canvas = document.createElement("canvas");
        canvas.width  = img.width  * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const RegistrationForm = ({ setUser, setActiveTab }) => {
  // Steps: 1=Your Details, 2=Verify Email, 3=Pet Details
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailExists, setEmailExists] = useState(false);

  const [customerInfo, setCustomerInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [pets, setPets] = useState([newPet()]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const errorKeys = Object.keys(formErrors).filter(k => !!formErrors[k]);
    if (errorKeys.length === 0) return;
    const firstKey = errorKeys.find(k => k !== 'submit');
    if (!firstKey) return;
    const activeEl = document.activeElement;
    const targetEl = document.getElementById(`field-${firstKey}`);
    if (targetEl && activeEl && targetEl.contains(activeEl)) return;

    const timer = setTimeout(() => {
      const el = document.getElementById(`field-${firstKey}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => clearTimeout(timer);
  }, [formErrors]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const validateStep1 = () => {
    const errors = {};
    if (!customerInfo.fullName.trim()) errors.fullName = "Full name is required";
    if (!customerInfo.email.trim()) errors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(customerInfo.email)) errors.email = "Invalid email address";
    const cleanPhone = customerInfo.phone.trim().replace(/\D/g, "");
    if (!cleanPhone) {
      errors.phone = "Phone number is required";
    } else if (!cleanPhone.startsWith("09")) {
      errors.phone = "Phone number must start with 09 (e.g. 09123456789)";
    } else if (cleanPhone.length !== 11) {
      errors.phone = "Phone number must be exactly 11 digits";
    }
    if (!customerInfo.password) errors.password = "Password is required";
    else if (customerInfo.password.length < 6) errors.password = "Password must be at least 6 characters";
    if (customerInfo.password !== customerInfo.confirmPassword) errors.confirmPassword = "Passwords do not match";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep3 = () => {
    const errors = {};
    pets.forEach((p, i) => {
      if (!p.petName.trim()) errors[`petName-${i}`] = "Pet name is required";
      if (!p.petType) errors[`petType-${i}`] = "Please select dog or cat";
      if (!p.isCustomBreed && !p.breed) errors[`breed-${i}`] = "Please select a breed";
      if (p.isCustomBreed && !p.customBreed.trim()) errors[`customBreed-${i}`] = "Please enter the breed";
      if (!p.birthday) errors[`birthday-${i}`] = "Birthdate is required";
      if (!p.healthStatus) errors[`healthStatus-${i}`] = "Please select your pet's health status";
      if (p.healthStatus === "not_healthy") {
        if (!p.medicalConditions?.trim()) errors[`medicalConditions-${i}`] = "Please describe your pet's medical condition";
        if (!p.medication?.trim()) errors[`medication-${i}`] = "Please enter the medication your pet is taking";
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Step 1 → send OTP to email → go to step 2
  const handleSendOtp = async () => {
    if (!validateStep1()) return;
    setLoading(true);
    setFormErrors({});
    setEmailExists(false);
    try {
      await axios.post("/api/auth/send-email-otp", { email: customerInfo.email });
      setResendCooldown(60);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "";
      if (msg.toLowerCase().includes("already exists")) {
        setEmailExists(true);
      } else {
        setFormErrors({ submit: msg || "Failed to send verification code" });
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2 → verify OTP → go to step 3
  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setFormErrors({ submit: "Please enter the verification code" });
      return;
    }
    setLoading(true);
    setFormErrors({});
    try {
      await axios.post("/api/auth/verify-email-otp", { email: customerInfo.email, otp });
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setFormErrors({ submit: err.response?.data?.message || err.message || "Verification failed" });
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    setFormErrors({});
    try {
      await axios.post("/api/auth/send-email-otp", { email: customerInfo.email });
      setResendCooldown(60);
      setOtp("");
    } catch (err) {
      setFormErrors({ submit: err.response?.data?.message || "Failed to resend code" });
    } finally {
      setLoading(false);
    }
  };

  // Step 3 → complete registration
  const handleSubmit = async () => {
    if (!validateStep3()) return;
    setLoading(true);
    setFormErrors({});
    try {
      const payload = {
        name: customerInfo.fullName,
        email: customerInfo.email,
        phone: customerInfo.phone,
        password: customerInfo.password,
        pets: pets.map(p => ({
          petName: p.petName,
          petType: p.petType,
          breed: p.isCustomBreed ? p.customBreed : p.breed,
          gender: p.gender,
          birthday: p.birthday || null,
          photoUrl: p.photoUrl,
          behaviorNotes: p.behaviorNotes,
          health: {
            isVaccinated: p.isVaccinated === "yes",
            lastVaccinationDate: p.lastVaccinationDate,
            vaccinationPhotoUrl: p.vaccinationPhotoUrl,
            healthStatus: p.healthStatus,
            medicalConditions: p.healthStatus === "not_healthy" ? p.medicalConditions : "",
            medication: p.healthStatus === "not_healthy" ? p.medication : ""
          }
        }))
      };

      const res = await axios.post("/api/auth/register", payload);
      if (res.data.success && res.data.token) {
        // Email pre-verified — account created and logged in
        localStorage.setItem("user", JSON.stringify(res.data));
        setUser(res.data);
        setSuccess(true);
      } else {
        // Unexpected: verification still required — go back to email step
        setFormErrors({ submit: "Email verification expired. Please go back and verify your email again." });
        setStep(2);
      }
    } catch (err) {
      setFormErrors({ submit: err.response?.data?.message || err.message || "Failed to register account" });
    } finally {
      setLoading(false);
    }
  };

  const updatePet = (index, fieldOrObj, value) => {
    setPets((prev) => {
      const updated = [...prev];
      if (typeof fieldOrObj === "object" && fieldOrObj !== null) {
        updated[index] = { ...updated[index], ...fieldOrObj };
      } else {
        updated[index] = { ...updated[index], [fieldOrObj]: value };
        if (fieldOrObj === "isCustomBreed" && value) updated[index].breed = "";
        else if (fieldOrObj === "isCustomBreed" && !value) updated[index].customBreed = "";
      }
      return updated;
    });
  };

  const addPet = () => setPets([...pets, newPet()]);
  const removePet = (index) => { if (pets.length > 1) setPets(pets.filter((_, i) => i !== index)); };

  const handleImageUpload = async (e, index, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await compressImage(file);
    updatePet(index, field, base64);
  };

  // Modal rendered ON TOP of form so there's no flash/remount
  // (success state keeps the form mounted beneath the overlay)

  const inputCls = "w-full px-5 py-4 border-2 border-purple-100 rounded-2xl outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-400/20 text-gray-700 bg-white font-medium transition-all shadow-sm hover:shadow-md";
  const errCls = "mt-2 text-xs text-red-500 font-bold bg-red-50 py-1.5 px-3 rounded-lg inline-block border border-red-100";
  const labelCls = "block text-sm font-bold text-gray-700 mb-2 ml-1";

  // Step labels
  const steps = [
    { label: "Your Details" },
    { label: "Verify Email" },
    { label: "Pet Details" },
  ];

  return (
    <div className="max-w-4xl mx-auto my-12 p-8 md:p-12 bg-white/70 backdrop-blur-2xl rounded-[3rem] shadow-2xl border border-white/50">
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-4">
          Create an Account
        </h2>
        <p className="text-gray-500 text-lg">Join the FurpectTails family today 🐾</p>

        {/* Step indicator */}
        <div className="flex justify-center mb-8 relative">
          <div className="flex items-center space-x-12 relative z-10">
            {steps.map((s, i) => {
              const stepNum = i + 1;
              const active = step >= stepNum;
              return (
                <div key={i} className={`flex flex-col items-center ${active ? 'text-purple-600' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-2 transition-all duration-300 ${active ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/40' : 'bg-gray-100'}`}>
                    {step > stepNum ? '✓' : stepNum}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider">{s.label}</span>
                </div>
              );
            })}
          </div>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 w-48 h-1 bg-gray-100 -z-0">
            <div className="h-full bg-purple-600 transition-all duration-500" style={{ width: step === 1 ? '0%' : step === 2 ? '50%' : '100%' }}></div>
          </div>
        </div>
      </div>

      {emailExists && step === 1 && (
        <div className="mb-8 p-5 bg-blue-50 border-2 border-blue-100 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">👤</span>
            <div>
              <p className="font-bold text-blue-800 text-sm">Account already exists</p>
              <p className="text-blue-600 text-sm">
                <strong>{customerInfo.email}</strong> is already registered. Please log in instead.
              </p>
            </div>
          </div>
          <button
            onClick={() => setActiveTab("login")}
            className="shrink-0 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl shadow hover:scale-105 transition-all text-sm"
          >
            Go to Login →
          </button>
        </div>
      )}

      {formErrors.submit && (
        <div className="mb-8 p-4 bg-red-50 border-2 border-red-100 text-red-600 rounded-2xl font-bold flex items-center">
          <span className="mr-3">⚠️</span> {formErrors.submit}
        </div>
      )}

      {/* ── Step 1: Your Details ── */}
      {step === 1 && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div id="field-fullName">
              <label className={labelCls}>Full Name *</label>
              <input type="text" className={inputCls} placeholder="John Doe" value={customerInfo.fullName} onChange={e => setCustomerInfo({...customerInfo, fullName: e.target.value})} />
              {formErrors.fullName && <p className={errCls}>{formErrors.fullName}</p>}
            </div>
            <div id="field-phone">
              <label className={labelCls}>Phone Number *</label>
              <input type="tel" className={inputCls} placeholder="09123456789" maxLength={11} value={customerInfo.phone} onChange={e => setCustomerInfo({...customerInfo, phone: e.target.value.replace(/\D/g, "").slice(0, 11)})} />
              {formErrors.phone && <p className={errCls}>{formErrors.phone}</p>}
            </div>
            <div id="field-email" className="md:col-span-2">
              <label className={labelCls}>Email Address *</label>
              <input type="email" className={inputCls} placeholder="john@example.com" value={customerInfo.email} onChange={e => { setCustomerInfo({...customerInfo, email: e.target.value}); setEmailExists(false); }} />
              {formErrors.email && <p className={errCls}>{formErrors.email}</p>}
              <p className="text-xs text-gray-400 mt-1.5 ml-1">A 6-digit verification code will be sent to this email</p>
            </div>
            <div id="field-password">
              <label className={labelCls}>Password *</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} className={inputCls} placeholder="••••••••" value={customerInfo.password} onChange={e => setCustomerInfo({...customerInfo, password: e.target.value})} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-500">
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {formErrors.password && <p className={errCls}>{formErrors.password}</p>}
            </div>
            <div id="field-confirmPassword">
              <label className={labelCls}>Confirm Password *</label>
              <div className="relative">
                <input type={showConfirmPassword ? "text" : "password"} className={inputCls} placeholder="••••••••" value={customerInfo.confirmPassword} onChange={e => setCustomerInfo({...customerInfo, confirmPassword: e.target.value})} />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-500">
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
              {formErrors.confirmPassword && <p className={errCls}>{formErrors.confirmPassword}</p>}
            </div>
          </div>
          <div className="flex justify-end pt-6 border-t border-purple-100">
            <button onClick={handleSendOtp} disabled={loading} className="px-10 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-2xl shadow-xl hover:scale-105 transition-all text-lg flex items-center gap-2 disabled:opacity-50">
              {loading ? "Sending Code..." : "Next: Verify Email"}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Verify Email OTP ── */}
      {step === 2 && (
        <div className="max-w-md mx-auto space-y-6 animate-in fade-in slide-in-from-right-8 duration-500">
          <div className="text-center space-y-2">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-4xl">
              ✉️
            </div>
            <h3 className="text-2xl font-black text-gray-800">Verify Your Email</h3>
            <p className="text-gray-500 text-sm">
              We've sent a 6-digit verification code to <br/>
              <span className="font-bold text-purple-600">{customerInfo.email}</span>
            </p>
            <p className="text-xs text-gray-400">Please check your inbox and spam/promotions folder</p>
          </div>

          <div className="bg-white p-8 rounded-3xl border-2 border-purple-50 shadow-sm space-y-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 text-center">Enter Verification Code</label>
              <input
                type="text"
                maxLength="6"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                className="w-full text-center text-3xl font-black tracking-[0.5em] p-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-purple-400 focus:bg-white outline-none transition-all"
                placeholder="------"
                autoFocus
              />
            </div>

            <button
              onClick={handleVerifyOtp}
              disabled={loading || otp.length < 6}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black rounded-2xl shadow-xl shadow-purple-200 hover:shadow-2xl hover:-translate-y-1 transition-all disabled:opacity-50 disabled:translate-y-0"
            >
              {loading ? "Verifying..." : "Verify Email ✓"}
            </button>

            <div className="text-center space-y-2">
              <p className="text-sm text-gray-500">
                Didn't receive the code?{" "}
                <button
                  onClick={handleResendOtp}
                  disabled={loading || resendCooldown > 0}
                  className="text-purple-600 font-bold hover:underline disabled:text-gray-400 disabled:no-underline"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Code"}
                </button>
              </p>
              <button onClick={() => { setStep(1); setOtp(""); setFormErrors({}); }} className="text-xs text-gray-400 hover:text-purple-500 transition-colors">
                ← Change email address
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Pet Details ── */}
      {step === 3 && (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
          {/* Verified badge */}
          <div className="flex items-center gap-2 justify-center mb-2">
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold px-4 py-1.5 rounded-full">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
              {customerInfo.email} verified
            </span>
          </div>

          {pets.map((p, index) => (
            <div key={index} className="bg-gradient-to-br from-white to-purple-50 p-6 sm:p-8 rounded-[2rem] border border-purple-100 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl group-hover:bg-pink-200/30 transition-colors"></div>
              
              <div className="flex justify-between items-center mb-6 border-b border-purple-100 pb-4 relative z-10">
                <h3 className="text-xl font-black text-purple-800 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm">{index + 1}</span>
                  Pet Profile
                </h3>
                {pets.length > 1 && (
                  <button onClick={() => removePet(index)} className="text-red-500 hover:text-red-700 font-bold text-sm bg-red-50 px-3 py-1.5 rounded-lg border border-red-100 hover:bg-red-100 transition-colors">
                    Remove Pet
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                <div id={`field-petName-${index}`}>
                  <label className={labelCls}>Pet's Name *</label>
                  <input type="text" className={inputCls} placeholder="e.g. Max" value={p.petName} onChange={(e) => updatePet(index, "petName", e.target.value)} />
                  {formErrors[`petName-${index}`] && <p className={errCls}>{formErrors[`petName-${index}`]}</p>}
                </div>
                
                <div id={`field-petType-${index}`}>
                  <label className={labelCls}>Pet Type *</label>
                  <div className="flex gap-3">
                    <button onClick={() => updatePet(index, "petType", "dog")} className={`flex-1 py-4 rounded-2xl font-bold flex flex-col items-center gap-2 transition-all border-2 ${p.petType === 'dog' ? 'bg-purple-100 border-purple-500 text-purple-700 shadow-inner' : 'bg-white border-purple-100 text-gray-500 hover:bg-purple-50'}`}>
                      <DogIcon size={24} color={p.petType === 'dog' ? "#7C3AED" : "currentColor"} /> Dog
                    </button>
                    <button onClick={() => updatePet(index, "petType", "cat")} className={`flex-1 py-4 rounded-2xl font-bold flex flex-col items-center gap-2 transition-all border-2 ${p.petType === 'cat' ? 'bg-pink-100 border-pink-500 text-pink-700 shadow-inner' : 'bg-white border-purple-100 text-gray-500 hover:bg-pink-50'}`}>
                      <CatIcon size={24} color={p.petType === 'cat' ? "#EC4899" : "currentColor"} /> Cat
                    </button>
                  </div>
                  {formErrors[`petType-${index}`] && <p className={errCls}>{formErrors[`petType-${index}`]}</p>}
                </div>

                <div id={`field-breed-${index}`}>
                  <label className={labelCls}>Breed *</label>
                  {!p.isCustomBreed ? (
                    <select className={inputCls} value={p.breed} onChange={(e) => updatePet(index, "breed", e.target.value)}>
                      <option value="">Select breed...</option>
                      {(p.petType === "cat" ? CAT_BREEDS : DOG_BREEDS).map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  ) : (
                    <input type="text" className={inputCls} placeholder="Enter breed" value={p.customBreed} onChange={(e) => updatePet(index, "customBreed", e.target.value)} />
                  )}
                  <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm font-medium text-gray-600">
                    <input type="checkbox" checked={p.isCustomBreed} onChange={(e) => updatePet(index, "isCustomBreed", e.target.checked)} className="w-4 h-4 text-purple-600 accent-purple-600" />
                    Breed not listed? Enter manually
                  </label>
                  {formErrors[`breed-${index}`] && <p className={errCls}>{formErrors[`breed-${index}`]}</p>}
                  {formErrors[`customBreed-${index}`] && <p className={errCls}>{formErrors[`customBreed-${index}`]}</p>}
                </div>

                <div>
                  <label className={labelCls}>Gender</label>
                  <select className={inputCls} value={p.gender} onChange={(e) => updatePet(index, "gender", e.target.value)}>
                    <option value="">Select gender...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Birthdate *</label>
                  <input
                    type="date"
                    className={`${inputCls} ${formErrors[`birthday-${index}`] ? "border-red-400 bg-red-50" : ""}`}
                    value={p.birthday}
                    onChange={(e) => updatePet(index, "birthday", e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                    required
                  />
                  {formErrors[`birthday-${index}`] && (
                    <p className="text-red-500 text-xs mt-1 font-bold">{formErrors[`birthday-${index}`]}</p>
                  )}
                  {p.birthday && !isPetEligibleForGrooming(p.birthday) && (
                    <p className="text-[11px] text-amber-700 font-semibold bg-amber-50 border border-amber-200 p-2 rounded-xl mt-1.5 flex items-start gap-1">
                      <span>⚠️</span>
                      <span>Notice: Furpect Tails is not accepting pets under 6 months old for grooming services.</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelCls}>Behavior Notes (Optional)</label>
                  <input type="text" className={inputCls} placeholder="e.g. anxious, friendly" value={p.behaviorNotes} onChange={(e) => updatePet(index, "behaviorNotes", e.target.value)} />
                </div>

                <div className="md:col-span-2">
                  <div className="bg-white/60 rounded-2xl p-4 border border-purple-100">
                    <label className={labelCls}>Pet Photo (Optional)</label>
                    <div className="flex items-center gap-4">
                      {p.photoUrl && <img src={p.photoUrl} alt="Pet" className="w-16 h-16 rounded-xl object-cover border-2 border-purple-200" />}
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, index, "photoUrl")} className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 bg-emerald-50/50 p-6 rounded-[2rem] border border-emerald-100">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-black uppercase tracking-widest text-emerald-600 flex items-center gap-2">🏥 Health Info</h4>
                    <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2.5 py-0.5 rounded-full border border-rose-200">Vaccination Required in Store</span>
                  </div>

                  {/* Vaccination policy notice */}
                  <div className="p-3 bg-amber-50/90 border border-amber-200 rounded-xl text-xs text-amber-900 mb-4 flex items-start gap-2">
                    <span className="text-sm shrink-0 mt-0.5">⚠️</span>
                    <p className="text-[11px] leading-relaxed">
                      <strong>Store Policy:</strong> For the protection of all pets, <strong>dogs and cats that are not vaccinated will NOT be accepted in the store</strong>. Up-to-date Anti-Rabies vaccination is required for all grooming and boarding visits.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Is your pet vaccinated?</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 font-medium cursor-pointer"><input type="radio" name={`vacc-${index}`} value="yes" checked={p.isVaccinated === "yes"} onChange={(e) => updatePet(index, "isVaccinated", e.target.value)} className="accent-emerald-500 w-4 h-4"/> Yes</label>
                        <label className="flex items-center gap-2 font-medium cursor-pointer"><input type="radio" name={`vacc-${index}`} value="no" checked={p.isVaccinated === "no"} onChange={(e) => updatePet(index, "isVaccinated", e.target.value)} className="accent-emerald-500 w-4 h-4"/> No</label>
                      </div>
                      {p.isVaccinated === "no" && (
                        <p className="text-[11px] font-bold text-red-600 mt-2 bg-red-50 p-2 rounded-lg border border-red-200">
                          ⚠️ Note: Pets without updated vaccines cannot be accepted in the store for services.
                        </p>
                      )}
                    </div>
                    {p.isVaccinated === "yes" && (
                      <>
                        <div>
                          <label className={labelCls}>Last Vaccination Date</label>
                          <input type="date" className={inputCls} value={p.lastVaccinationDate} onChange={(e) => updatePet(index, "lastVaccinationDate", e.target.value)} />
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelCls}>Vaccination Record Photo (Optional)</label>
                          <div className="flex items-center gap-4">
                            {p.vaccinationPhotoUrl && <img src={p.vaccinationPhotoUrl} alt="Vaccine" className="w-16 h-16 rounded-xl object-cover border-2 border-emerald-200" />}
                            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, index, "vaccinationPhotoUrl")} className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100" />
                          </div>
                        </div>
                      </>
                    )}
                    <div className="md:col-span-2 space-y-3">
                      <label className={labelCls}>Health Status <span className="text-red-500">*</span></label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            updatePet(index, { healthStatus: "healthy", medicalConditions: "", medication: "" });
                            setFormErrors((prev) => {
                              const next = { ...prev };
                              delete next[`healthStatus-${index}`];
                              delete next[`medicalConditions-${index}`];
                              delete next[`medication-${index}`];
                              return next;
                            });
                          }}
                          className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 font-bold text-sm transition-all ${
                            p.healthStatus === "healthy"
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm"
                              : "border-gray-200 bg-white text-gray-500 hover:border-emerald-300"
                          }`}
                        >
                          <span>✅</span> Healthy
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            updatePet(index, "healthStatus", "not_healthy");
                            setFormErrors((prev) => {
                              const next = { ...prev };
                              delete next[`healthStatus-${index}`];
                              return next;
                            });
                          }}
                          className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 font-bold text-sm transition-all ${
                            p.healthStatus === "not_healthy"
                              ? "border-rose-500 bg-rose-50 text-rose-700 shadow-sm"
                              : "border-gray-200 bg-white text-gray-500 hover:border-rose-300"
                          }`}
                        >
                          <span>🩺</span> Not Healthy
                        </button>
                      </div>
                      {formErrors[`healthStatus-${index}`] && <p className="text-red-500 text-xs font-bold">{formErrors[`healthStatus-${index}`]}</p>}
                    </div>

                    {p.healthStatus === "not_healthy" && (
                      <>
                        <div className="md:col-span-2 space-y-2">
                          <label className={labelCls}>Medical Condition / Notes <span className="text-red-500">*</span></label>
                          <textarea
                            rows={3}
                            className={`${inputCls} resize-none`}
                            placeholder="Please describe your pet's current condition (e.g. skin allergy, diabetes, recovering from surgery...)"
                            value={p.medicalConditions}
                            onChange={(e) => updatePet(index, "medicalConditions", e.target.value)}
                          />
                          {formErrors[`medicalConditions-${index}`] && <p className="text-red-500 text-xs font-bold">{formErrors[`medicalConditions-${index}`]}</p>}
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <label className={labelCls}>Medication <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            className={`${inputCls} ${formErrors[`medication-${index}`] ? "border-red-400 bg-red-50" : ""}`}
                            placeholder="e.g. Amoxicillin 250mg twice daily"
                            value={p.medication}
                            onChange={(e) => updatePet(index, "medication", e.target.value)}
                          />
                          {formErrors[`medication-${index}`] && <p className="text-red-500 text-xs font-bold">{formErrors[`medication-${index}`]}</p>}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button onClick={addPet} className="w-full py-4 border-2 border-dashed border-purple-300 rounded-[2rem] text-purple-600 font-bold hover:bg-purple-50 transition-colors flex items-center justify-center gap-2">
            + Add Another Pet
          </button>

          <div className="flex justify-between pt-6 border-t border-purple-100">
            <button onClick={() => { setStep(2); setOtp(""); setFormErrors({}); }} className="px-8 py-4 bg-white border-2 border-purple-100 text-purple-600 font-bold rounded-2xl hover:bg-purple-50 transition-colors">
              Back
            </button>
            <button onClick={handleSubmit} disabled={loading} className="px-10 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-xl hover:scale-105 transition-all text-lg disabled:opacity-50">
              {loading ? "Creating Account..." : "Complete Registration 🎉"}
            </button>
          </div>
        </div>
      )}

      {/* ── Account Created Modal Overlay ── */}
      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />

          {/* Modal card */}
          <div className="relative z-10 w-full max-w-lg bg-white rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-90 duration-300">

            {/* Top gradient banner */}
            <div className="bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-600 px-10 pt-10 pb-14 text-center relative overflow-hidden">
              {/* Decorative blobs */}
              <div className="absolute -top-8 -left-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />

              <div className="relative z-10">
                <div className="w-24 h-24 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4 text-5xl shadow-lg ring-4 ring-white/30">
                  🎉
                </div>
                <h2 className="text-3xl font-black text-white mb-2 drop-shadow">Account Created!</h2>
                <p className="text-purple-200 text-sm font-medium">
                  Welcome to FurpectTails 🐾 You're all set.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="px-10 py-8 -mt-6 relative z-10">
              {/* White pill card */}
              <div className="bg-white rounded-2xl shadow-lg border border-purple-50 p-5 mb-6 text-center">
                <p className="text-gray-600 text-sm leading-relaxed">
                  Your account and pet profile have been saved. What would you like to do next?
                </p>
              </div>

              <div className="space-y-3">
                {/* Book an appointment */}
                <button
                  onClick={() => setActiveTab("booking")}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black text-lg rounded-2xl shadow-xl shadow-purple-200 hover:shadow-2xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
                >
                  <span>📅</span>
                  Book an Appointment
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14m-7-7 7 7-7 7" />
                  </svg>
                </button>

                {/* Go to dashboard */}
                <button
                  onClick={() => setActiveTab("dashboard")}
                  className="w-full py-4 bg-white border-2 border-purple-100 text-purple-700 font-bold text-base rounded-2xl hover:bg-purple-50 hover:border-purple-300 transition-all flex items-center justify-center gap-2"
                >
                  <span>🏠</span>
                  Go to My Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;
